// hooks/useCreateNewReservation.ts
import { useDispatch } from "react-redux";
import { baseStore } from "../store"; // Adjust the import based on your file structure
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { rentalsApiSlice } from "../../api/rentals/rentalsApiSlice";
import { setRentalFinished } from "../slice/booking/bookingSlice";
import { Rental } from "../../types/rental";
import { bookingsApiSlice } from "../../api/bookings/bookingsApiSlice";
import { enqueueNotification } from "../slice/notification/notificationSlice";
import { getI18n, useTranslation } from "react-i18next";

// Returns true when a photo was submitted but the backend reported it could
// not be saved (rental still finished). Callers use this to warn the customer
// instead of letting the missing photo become a silent error.
export const finishRentalCall = async (
  rental: Rental,
  photo?: File,
  finishMethod?: string,
  skipped?: boolean
): Promise<{ photoUploadFailed: boolean }> => {
  try {
    let photoFinishFailed = false;
    if (photo) {
      const result = await baseStore.dispatch(
        rentalsApiSlice.endpoints.finishRentalViaToken.initiate({
          finishRentalToken: rental.token,
          photo,
        })
      );
      if ("error" in result && result.error) {
        // The photo-finish request died (payload rejected, Lambda timeout,
        // expired token, flaky network …). The photo must never block the
        // return: fall through to the photo-less finish below and let the
        // caller show the "photo not saved" warning (S-00000232A).
        Logger.for("finishRental").error(
          `finish-with-photo failed for orderId ${rental.orderId}, ` +
            `falling back to photo-less finish`,
          result.error
        );
        photoFinishFailed = true;
      } else {
        const photoUploadFailed =
          "data" in result && result.data?.photoUploadFailed === true;
        return { photoUploadFailed };
      }
    }

    const result = await baseStore.dispatch(
      rentalsApiSlice.endpoints.finishRental.initiate({
        orderId: rental.orderId,
        items: [rental.orderItemId],
        token: rental.token,
        finishMethod,
        skipped,
      })
    );
    if ("error" in result && result.error) {
      throw new Error(`Failed to finish rental. Error: ${result.error}`);
    }
    return { photoUploadFailed: photoFinishFailed };
  } catch (error) {
    throw new Error(
      `Failed to finish rental for orderId ${rental.orderId}: ${error}`
    );
  }
};

export const useFinishRental = (
  rental: Rental | undefined,
  isPlacingItem?: boolean,
  isSelectedBookingHostKeyRetrieval?: boolean
) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const finishRental = async (
    photo?: File,
    finishMethod?: string,
    skipped?: boolean
  ) => {
    try {
      if (rental) {
        const { photoUploadFailed } = await finishRentalCall(
          rental,
          photo,
          finishMethod,
          skipped
        );
        // Force a fresh fetch instead of reusing the cached order: the
        // cached entry predates the finish call and still lacks
        // `finishedAtDate`, which makes the status derive to "booked"
        // ("Gebucht") instead of "finished" ("Beendet") on the
        // self-confirm path (see #1035 item 7).
        const result: any = await baseStore.dispatch(
          bookingsApiSlice.endpoints.getUserOrder.initiate(
            { orderIds: rental.orderId },
            { forceRefetch: true }
          )
        );

        if (result && result.data && result.data[0]) {
          dispatch(
            setRentalFinished({
              orderId: rental.orderId,
              updatedOrder: result.data[0],
            })
          );
          if (isSelectedBookingHostKeyRetrieval) {
            dispatch(
              enqueueNotification({
                severity: "success",
                message: t("use_order.handovers.item_placement.key_placement_success"),
              })
            );
          } else if (isPlacingItem) {
            dispatch(
              enqueueNotification({
                severity: "success",
                message: t("use_order.handovers.item_placement.success"),
              })
            );
          } else if (photoUploadFailed) {
            // Rental finished, but the return photo was not saved — tell the
            // customer so the missing receipt isn't a silent error.
            dispatch(
              enqueueNotification({
                severity: "warning",
                message: t("rental.return.photo.save_failed"),
              })
            );
          } else {
            dispatch(
              enqueueNotification({
                severity: "success",
                message: getI18n().t(`Your rental has successfully ended.`),
              })
            );
          }
        } else {
          throw "Error while fetching the new order data";
        }
      }
    } catch (error) {
      Logger.for("finishRental").error(error);
      ErrorTracker.trackException(error as Error);
      dispatch(
        enqueueNotification({
          severity: "error",
          message: getI18n().t("use_order.stop_using.error"),
        })
      );
      // Propagate the error
      throw "Error while fetching the new order data";
    }
  };

  return finishRental;
};
